import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { tokenManager } from '../../utils/tokenManager';
import { AppDispatch } from '../store'; // Adjust the path as necessary
import { registerPlanSuccess, registerPlanFailure, setSearchResults, clearSearchResults } from '../slices/planSlice';

// Create a single axios instance
const api = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true,
});

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const getTokenWithRetry = async (retries = 0): Promise<string | null> => {
  try {
    const token = await tokenManager.getToken();
    if (token) {
      return token;
    }
    if (retries < MAX_RETRIES) {
      console.log(`Token not found, retrying in ${RETRY_DELAY}ms...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return getTokenWithRetry(retries + 1);
    }
    console.log('Max retries reached, no token found');
    return null;
  } catch (error) {
    console.error('Error in getTokenWithRetry:', error);
    return null;
  }
};

// Add an interceptor to include the JWT and auth type in every request
api.interceptors.request.use(
  async (config) => {
    const token = await getTokenWithRetry();
    const authType = tokenManager.getAuthType();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (authType) {
      config.headers['X-Auth-Type'] = authType.toLowerCase();  // Ensure lowercase
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add an interceptor for logging responses (for debugging purposes)
api.interceptors.response.use(response => {
  return response
}, error => {
  return Promise.reject(error)
})

// Add this new action type
export const CLEAR_ALL_MESSAGES = 'CLEAR_ALL_MESSAGES';

// Add this new action creator
export const clearAllMessages = () => ({
  type: CLEAR_ALL_MESSAGES
});

// Update PlanActionTypes
export type PlanActionTypes =
  ReturnType<typeof clearRegistrationMessage> |
  ReturnType<typeof clearError> |
  ReturnType<typeof clearAllMessages> |
  ReturnType<typeof setError>;

// Modify the searchPlans thunk
export const searchPlans = (searchParams: any) => async (dispatch: AppDispatch) => {
  try {
    //console.log('Sending search request with params:', searchParams);
    const token = await tokenManager.getToken();
    const authType = tokenManager.getAuthType();
    //console.log('Token for search request:', token);
    //console.log('Auth type for search request:', authType);

    const response = await api.post('/search', searchParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Auth-Type': authType,
      },
    });

    //console.log('Received search response:', response.data);
    dispatch(setSearchResults(response.data));
  } catch (error) {
    console.error('Error in searchPlans:', error);
    dispatch(setError('An error occurred while searching for plans.'));
  }
};

// Add this new action creator for setting errors
export const setError = (error: string) => ({
  type: 'SET_ERROR',
  payload: error
});

export const registerPlan = (planData: any) => async (dispatch: AppDispatch) => {
  try {
    const token = await getTokenWithRetry();
    const authType = tokenManager.getAuthType();
    //console.log('Token for register request:', token);
    //console.log('Auth type for register request:', authType);

    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await api.post('/register', planData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Auth-Type': authType,
      },
    });

    //console.log('Received register response:', response.data);
    dispatch(registerPlanSuccess(response.data));
  } catch (error) {
    console.error('Error in registerPlan:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    dispatch(registerPlanFailure(errorMessage));
  }
};
// Add this new action type
export const CLEAR_REGISTRATION_MESSAGE = 'CLEAR_REGISTRATION_MESSAGE';

// Add this new action creator
export const clearRegistrationMessage = () => ({
  type: CLEAR_REGISTRATION_MESSAGE as typeof CLEAR_REGISTRATION_MESSAGE
});

// Add this new action type
export const CLEAR_ERROR = 'CLEAR_ERROR';

// Add this new action creator
export const clearError = () => ({
  type: CLEAR_ERROR as typeof CLEAR_ERROR
});

// Remove the duplicate PlanActionTypes definition
// The existing PlanActionTypes definition above is sufficient

// Add this export at the end of the file
export { clearSearchResults };

